import { supabase } from '../lib/supabase';

export const voucherService = {
  async getAll(filters = {}) {
    let query = supabase?.from('vouchers')?.select(`
        *,
        seller:user_profiles!seller_id(id, full_name, rating, is_verified),
        category:categories(id, name),
        images:voucher_images(id, image_url, is_primary, display_order),
        external_links:voucher_external_links(id, link_type, link_url, display_order)
      `)?.eq('voucher_status', 'active')?.order('created_at', { ascending: false });

    if (filters?.categoryId) {
      query = query?.eq('category_id', filters?.categoryId);
    }

    if (filters?.minPrice) {
      query = query?.gte('selling_price', filters?.minPrice);
    }

    if (filters?.maxPrice) {
      query = query?.lte('selling_price', filters?.maxPrice);
    }

    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%,company_name.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    return data?.map(voucher => ({
      id: voucher?.id,
      sellerId: voucher?.seller_id,
      categoryId: voucher?.category_id,
      title: voucher?.title,
      description: voucher?.description,
      shortDescription: voucher?.short_description,
      symbol: voucher?.symbol,
      totalSupply: voucher?.total_supply,
      currency: voucher?.currency,
      earningRules: voucher?.earning_rules,
      spendingRules: voucher?.spending_rules,
      originalPrice: voucher?.original_price,
      sellingPrice: voucher?.selling_price,
      discountPercentage: voucher?.discount_percentage,
      companyName: voucher?.company_name,
      expiryDate: voucher?.expiry_date,
      voucherStatus: voucher?.voucher_status,
      termsConditions: voucher?.terms_conditions,
      viewsCount: voucher?.views_count,
      createdAt: voucher?.created_at,
      updatedAt: voucher?.updated_at,
      seller: voucher?.seller ? {
        id: voucher?.seller?.id,
        fullName: voucher?.seller?.full_name,
        rating: voucher?.seller?.rating,
        isVerified: voucher?.seller?.is_verified
      } : null,
      category: voucher?.category ? {
        id: voucher?.category?.id,
        name: voucher?.category?.name
      } : null,
      images: voucher?.images?.map(img => ({
        id: img?.id,
        imageUrl: img?.image_url,
        isPrimary: img?.is_primary,
        displayOrder: img?.display_order
      })) || [],
      externalLinks: voucher?.external_links?.sort((a, b) => a?.display_order - b?.display_order)?.map(link => ({
        id: link?.id,
        linkType: link?.link_type,
        linkUrl: link?.link_url,
        displayOrder: link?.display_order
      })) || []
    }));
  },

  async getById(voucherId) {
    const { data, error } = await supabase?.from('vouchers')?.select(`
        *,
        seller:user_profiles!seller_id(id, full_name, rating, is_verified, phone),
        category:categories(id, name),
        images:voucher_images(id, image_url, is_primary, display_order),
        external_links:voucher_external_links(id, link_type, link_url, display_order)
      `)?.eq('id', voucherId)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      sellerId: data?.seller_id,
      categoryId: data?.category_id,
      title: data?.title,
      description: data?.description,
      shortDescription: data?.short_description,
      symbol: data?.symbol,
      totalSupply: data?.total_supply,
      currency: data?.currency,
      earningRules: data?.earning_rules,
      spendingRules: data?.spending_rules,
      originalPrice: data?.original_price,
      sellingPrice: data?.selling_price,
      discountPercentage: data?.discount_percentage,
      companyName: data?.company_name,
      expiryDate: data?.expiry_date,
      voucherStatus: data?.voucher_status,
      termsConditions: data?.terms_conditions,
      viewsCount: data?.views_count,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      seller: data?.seller ? {
        id: data?.seller?.id,
        fullName: data?.seller?.full_name,
        rating: data?.seller?.rating,
        isVerified: data?.seller?.is_verified,
        phone: data?.seller?.phone
      } : null,
      category: data?.category ? {
        id: data?.category?.id,
        name: data?.category?.name
      } : null,
      images: data?.images?.map(img => ({
        id: img?.id,
        imageUrl: img?.image_url,
        isPrimary: img?.is_primary,
        displayOrder: img?.display_order
      })) || [],
      externalLinks: data?.external_links?.sort((a, b) => a?.display_order - b?.display_order)?.map(link => ({
        id: link?.id,
        linkType: link?.link_type,
        linkUrl: link?.link_url,
        displayOrder: link?.display_order
      })) || []
    };
  },

  async create(voucherData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Start transaction by creating voucher first
    const { data: voucher, error: voucherError } = await supabase?.from('vouchers')?.insert({
        seller_id: user?.id,
        category_id: voucherData?.categoryId,
        title: voucherData?.title,
        description: voucherData?.description,
        short_description: voucherData?.shortDescription,
        symbol: voucherData?.symbol,
        total_supply: voucherData?.totalSupply,
        currency: voucherData?.currency || 'RSD',
        earning_rules: voucherData?.earningRules,
        spending_rules: voucherData?.spendingRules,
        original_price: voucherData?.originalPrice,
        selling_price: voucherData?.sellingPrice,
        company_name: voucherData?.companyName,
        expiry_date: voucherData?.expiryDate,
        terms_conditions: voucherData?.termsConditions,
        voucher_status: 'pending'
      })?.select()?.single();
    
    if (voucherError) throw voucherError;

    // If external links are provided, insert them
    if (voucherData?.externalLinks && voucherData?.externalLinks?.length > 0) {
      const linksToInsert = voucherData?.externalLinks?.map((link, index) => ({
        voucher_id: voucher?.id,
        link_type: link?.linkType,
        link_url: link?.linkUrl,
        display_order: index + 1
      }));

      const { error: linksError } = await supabase?.from('voucher_external_links')?.insert(linksToInsert);
      
      if (linksError) {
        // Rollback by deleting the voucher if links fail
        await supabase?.from('vouchers')?.delete()?.eq('id', voucher?.id);
        throw linksError;
      }
    }
    
    return voucher;
  },

  async getCategories() {
    // Fetch all active categories with parent information for hierarchical display
    const { data, error } = await supabase
      ?.from('categories')
      ?.select('id, name, description, icon_url, is_active, created_at, parent_id')
      ?.eq('is_active', true)
      ?.order('name');
    
    if (error) throw error;
    
    return data?.map(cat => ({
      id: cat?.id,
      name: cat?.name,
      description: cat?.description,
      iconUrl: cat?.icon_url,
      isActive: cat?.is_active,
      createdAt: cat?.created_at,
      parentId: cat?.parent_id
    }));
  },

  async incrementViews(voucherId) {
    const { error } = await supabase?.rpc('increment_voucher_views', {
      voucher_id: voucherId
    });
    
    if (error) {
      const { error: updateError } = await supabase?.from('vouchers')?.update({ views_count: supabase?.raw('views_count + 1') })?.eq('id', voucherId);
      
      if (updateError) throw updateError;
    }
  },

  async getWithFilters(filters = {}) {
    try {
      let query = supabase?.from('vouchers')?.select(`
          *,
          category:categories(id, name, description),
          seller:user_profiles(id, full_name, email)
        `);
      
      // Filter by search term (title or company_name)
      if (filters?.searchTerm) {
        query = query?.or(`title.ilike.%${filters?.searchTerm}%,company_name.ilike.%${filters?.searchTerm}%,symbol.ilike.%${filters?.searchTerm}%`);
      }
      
      // Filter by availability type
      if (filters?.availabilityType && filters?.availabilityType !== 'all') {
        query = query?.eq('availability_type', filters?.availabilityType);
      }
      
      // Filter by category
      if (filters?.categoryId && filters?.categoryId !== 'all') {
        query = query?.eq('category_id', filters?.categoryId);
      }
      
      // Filter by issuer (company name)
      if (filters?.issuer) {
        query = query?.ilike('company_name', `%${filters?.issuer}%`);
      }
      
      // Only show active vouchers
      query = query?.eq('voucher_status', 'active');
      
      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching vouchers with filters:', error);
      throw error;
    }
  },

  async getUserVouchers(userId) {
    const { data, error } = await supabase?.from('vouchers')?.select(`
        *,
        category:categories(id, name),
        images:voucher_images(id, image_url, is_primary, display_order)
      `)?.eq('seller_id', userId)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(voucher => ({
      id: voucher?.id,
      sellerId: voucher?.seller_id,
      categoryId: voucher?.category_id,
      title: voucher?.title,
      description: voucher?.description,
      originalPrice: voucher?.original_price,
      sellingPrice: voucher?.selling_price,
      discountPercentage: voucher?.discount_percentage,
      companyName: voucher?.company_name,
      expiryDate: voucher?.expiry_date,
      voucherStatus: voucher?.voucher_status,
      termsConditions: voucher?.terms_conditions,
      viewsCount: voucher?.views_count,
      createdAt: voucher?.created_at,
      updatedAt: voucher?.updated_at,
      category: voucher?.category ? {
        id: voucher?.category?.id,
        name: voucher?.category?.name
      } : null,
      images: voucher?.images?.map(img => ({
        id: img?.id,
        imageUrl: img?.image_url,
        isPrimary: img?.is_primary,
        displayOrder: img?.display_order
      })) || []
    })) || [];
  }
};